import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Camera, Check, Globe, Images, Instagram, MapPin, Radius } from 'lucide-react';
import { SEO } from '@/components/SEO';
import { Reveal } from '@/components/Reveal';
import { TextReveal } from '@/components/TextReveal';
import { Lightbox } from '@/components/Lightbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar } from '@/components/ui/calendar';
import { VerifiedBadge } from '@/components/marketplace/VerifiedBadge';
import { StarRating } from '@/components/marketplace/StarRating';
import { getPhotographerProfile, getPhotographerReviews } from '@/lib/api';
import { accentStyle, formatPriceFrom } from '@/lib/studioAccent';
import { parseIsoDate } from '@/lib/dateUtils';
import { PHOTOGRAPHER_PROFILE } from '@/constants/testIds';
import NotFound from '@/pages/NotFound';
import Masonry from 'react-masonry-css';
import '@/styles/studioAccent.css';

const FALLBACK_COVER = 'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?crop=entropy&cs=srgb&fm=jpg&w=2400&q=75';
const BREAKPOINTS = { default: 3, 1024: 3, 768: 2, 500: 1 };

const SectionHeader = ({ kicker, title }) => (
  <div className="mb-8">
    <p className="text-xs tracking-[0.22em] uppercase studio-accent-text">{kicker}</p>
    <h2 className="font-display text-3xl sm:text-4xl tracking-[0.06em] text-[hsl(var(--lxra-fg))] mt-2">{title}</h2>
    <div className="mt-4 h-[2px] w-12 studio-accent-divider" />
  </div>
);

const money = (c) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

export default function PhotographerProfile() {
  const { handle } = useParams();
  const [studio, setStudio] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStudio(null); setNotFound(false); setReviews(null);
    getPhotographerProfile(handle).then((d) => { if (!cancelled) setStudio(d); }).catch(() => { if (!cancelled) setNotFound(true); });
    return () => { cancelled = true; };
  }, [handle]);

  useEffect(() => {
    if (!studio) return;
    let cancelled = false;
    getPhotographerReviews(studio.handle).then((d) => { if (!cancelled) setReviews(d); }).catch(() => { if (!cancelled) setReviews({ average: null, count: 0, reviews: [] }); });
    return () => { cancelled = true; };
  }, [studio?.handle]);

  const unavailableDates = useMemo(() => (studio?.unavailable_dates || []).map(parseIsoDate).filter(Boolean), [studio]);

  const portfolioImages = useMemo(() => (studio?.portfolio || []).map((p, i) => ({ id: p.image_id || `${i}-${p.image_url}`, image_url: p.image_url, caption: p.caption })), [studio]);

  if (notFound) return <NotFound />;

  if (!studio) {
    return (
      <div className="pt-16 min-h-[70vh] bg-[#0A0A0A]" data-testid={PHOTOGRAPHER_PROFILE.root}>
        <Skeleton className="h-[52vh] w-full bg-[#141416]" />
        <div className="mx-auto max-w-6xl px-4 py-12 space-y-4"><Skeleton className="h-8 w-1/2 bg-[#141416]" /><Skeleton className="h-40 bg-[#141416] rounded-2xl" /></div>
      </div>
    );
  }

  const bookHref = `/book?photographer=${studio.handle}`;
  const priceFrom = formatPriceFrom(studio.price_from_cents);
  const highlightId = studio.packages?.[0]?.id;
  const profileDescription = studio.tagline || studio.bio?.slice(0, 150) || `Book ${studio.name}, an LXRA Marketplace photographer serving ${studio.service_area || 'the DMV'}.`;
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: studio.name,
    image: studio.cover_url || studio.logo_url || undefined,
    description: profileDescription,
    areaServed: studio.service_area || studio.home_base?.label || undefined,
    priceRange: priceFrom ? `From ${priceFrom}` : undefined,
    sameAs: [studio.instagram ? `https://instagram.com/${studio.instagram}` : null, studio.website || null].filter(Boolean),
    ...(studio.rating_count > 0
      ? { aggregateRating: { '@type': 'AggregateRating', ratingValue: studio.rating_average, reviewCount: studio.rating_count } }
      : {}),
  };

  return (
    <main data-studio-profile="true" data-testid={PHOTOGRAPHER_PROFILE.root} style={accentStyle(studio.accent_color)} className="bg-[#0A0A0A]">
      <SEO title={`${studio.name} — LXRA Photographer`} description={profileDescription} image={studio.cover_url || undefined} structuredData={structuredData} />

      {/* Hero */}
      <section data-testid={PHOTOGRAPHER_PROFILE.hero} className="relative min-h-[72vh] flex items-end lxra-grain overflow-hidden">
        <img src={studio.cover_url || FALLBACK_COVER} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/65 to-[#0A0A0A]/25" />
        <div className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-10 pb-14 pt-36">
          <Reveal>
            <Link to="/photographers" data-testid={PHOTOGRAPHER_PROFILE.backLink} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] transition-colors mb-8">
              <ArrowLeft className="h-4 w-4" /> All photographers
            </Link>
            <div className="flex items-end gap-5">
              <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl border border-white/15 bg-black/60 backdrop-blur flex items-center justify-center overflow-hidden flex-shrink-0">
                {studio.logo_url ? <img src={studio.logo_url} alt={`${studio.name} logo`} className="h-full w-full object-cover" /> : <Camera className="h-8 w-8 studio-accent-text" />}
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))]">LXRA Photographer</p>
                  {studio.verified && <VerifiedBadge testId={PHOTOGRAPHER_PROFILE.verifiedBadge} />}
                  {studio.rating_count > 0 && (
                    <span data-testid={PHOTOGRAPHER_PROFILE.ratingBadge} className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--lxra-fg))]">
                      <StarRating value={studio.rating_average} size="h-4 w-4" />
                      <span className="lxra-mono">{studio.rating_average}</span>
                      <span className="text-[hsl(var(--lxra-muted))]">({studio.rating_count})</span>
                    </span>
                  )}
                </div>
                <h1 data-testid={PHOTOGRAPHER_PROFILE.name} className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-[0.06em] leading-[0.95] text-[hsl(var(--lxra-fg))] mt-2">
                  <TextReveal text={studio.name.toUpperCase()} />
                </h1>
              </div>
            </div>
            <div className="mt-4 h-[2px] w-16 studio-accent-divider" />
            {studio.tagline && <p data-testid={PHOTOGRAPHER_PROFILE.tagline} className="mt-5 max-w-2xl text-base sm:text-lg text-[hsl(var(--lxra-muted))] leading-relaxed">{studio.tagline}</p>}
            <p className="mt-3 inline-flex items-center gap-2 text-sm text-[hsl(var(--lxra-fg))]">
              <MapPin className="h-4 w-4 studio-accent-text" /> {studio.service_area || studio.home_base?.label || 'DMV'}
              {priceFrom && <span className="text-[hsl(var(--lxra-muted))]">· from {priceFrom}</span>}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={bookHref} data-testid={PHOTOGRAPHER_PROFILE.requestButton} className="studio-solid-btn studio-accent-ring inline-flex items-center h-12 px-7 rounded-xl text-xs uppercase tracking-[0.1em] font-semibold">
                Request to book <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              {portfolioImages.length > 0 && (
                <a href="#portfolio" data-testid={PHOTOGRAPHER_PROFILE.portfolioButton} className="studio-outline-btn studio-accent-ring inline-flex items-center h-12 px-6 rounded-xl text-xs uppercase tracking-[0.1em] font-medium">
                  <Images className="mr-2 h-4 w-4" /> View portfolio
                </a>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Bio + facts */}
      <section data-testid={PHOTOGRAPHER_PROFILE.bioSection} className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-3">
            <SectionHeader kicker="About" title="THE PHOTOGRAPHER." />
            {studio.bio ? (
              <div className="space-y-4">
                {studio.bio.split(/\n\n+/).map((p) => <p key={p.slice(0, 60)} className="text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed">{p}</p>)}
              </div>
            ) : (
              <p className="text-sm text-[hsl(var(--lxra-muted))]">This photographer hasn’t written a bio yet.</p>
            )}
            {studio.styles?.length > 0 && (
              <div data-testid={PHOTOGRAPHER_PROFILE.styleChips} className="mt-8 flex flex-wrap gap-2">
                {studio.styles.map((s) => (
                  <span key={s} className="studio-accent-border studio-accent-soft-bg rounded-full border px-3 py-1 text-[11px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-fg))]">{s}</span>
                ))}
              </div>
            )}
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-2">
            <div data-testid={PHOTOGRAPHER_PROFILE.facts} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-6 space-y-5">
              <p className="text-xs tracking-[0.18em] uppercase studio-accent-text">Quick facts</p>
              <dl className="space-y-4 text-sm">
                <div><dt className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--lxra-muted))]">Home base</dt><dd className="mt-1 text-[hsl(var(--lxra-fg))] flex items-center gap-2"><MapPin className="h-3.5 w-3.5 studio-accent-text" />{studio.home_base?.label || studio.service_area || '—'}</dd></div>
                <div><dt className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--lxra-muted))]">Travels</dt><dd className="mt-1 text-[hsl(var(--lxra-fg))] flex items-center gap-2"><Radius className="h-3.5 w-3.5 studio-accent-text" />Up to {studio.travel_radius_miles} miles</dd></div>
                {priceFrom && <div><dt className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--lxra-muted))]">Sessions from</dt><dd className="mt-1 text-[hsl(var(--lxra-fg))]">{priceFrom}</dd></div>}
                {(studio.instagram || studio.website) && (
                  <div>
                    <dt className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--lxra-muted))]">Links</dt>
                    <dd className="mt-2 flex flex-wrap gap-2">
                      {studio.instagram && <a href={`https://instagram.com/${studio.instagram}`} target="_blank" rel="noopener noreferrer" data-testid={PHOTOGRAPHER_PROFILE.instagramLink} className="studio-outline-btn inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[11px] uppercase tracking-[0.1em]"><Instagram className="h-3.5 w-3.5" /> @{studio.instagram}</a>}
                      {studio.website && <a href={studio.website.startsWith('http') ? studio.website : `https://${studio.website}`} target="_blank" rel="noopener noreferrer" data-testid={PHOTOGRAPHER_PROFILE.websiteLink} className="studio-outline-btn inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[11px] uppercase tracking-[0.1em]"><Globe className="h-3.5 w-3.5" /> Website</a>}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Availability */}
      <section data-testid={PHOTOGRAPHER_PROFILE.availabilitySection} className="border-t border-[hsl(var(--lxra-border))]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
          <Reveal><SectionHeader kicker="Availability" title="OPEN DATES." /></Reveal>
          <Reveal delay={0.08} className="flex flex-col sm:flex-row items-start gap-6">
            <Calendar
              numberOfMonths={2}
              modifiers={{ unavailable: unavailableDates }}
              modifiersClassNames={{ unavailable: 'bg-red-500/20 text-red-300 pointer-events-none' }}
              fromMonth={new Date()}
              className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] mx-auto sm:mx-0"
            />
            <div className="flex items-center gap-6 text-xs text-[hsl(var(--lxra-muted))] sm:pt-2">
              <p className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-red-400" /> Unavailable</p>
              <p className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full border border-[hsl(var(--lxra-border))]" /> Open</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Portfolio */}
      {portfolioImages.length > 0 && (
        <section id="portfolio" data-testid={PHOTOGRAPHER_PROFILE.portfolioSection} className="border-t border-[hsl(var(--lxra-border))]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
            <Reveal><SectionHeader kicker="Portfolio" title="SELECTED WORK." /></Reveal>
            <Masonry breakpointCols={BREAKPOINTS} className="masonry-grid" columnClassName="masonry-grid_column">
              {portfolioImages.map((img, i) => (
                <Reveal key={img.id} delay={(i % 6) * 0.04}>
                  <button type="button" onClick={() => setLightbox(i)} data-testid={PHOTOGRAPHER_PROFILE.portfolioImage(i)} className="studio-portfolio-tile group relative block w-full overflow-hidden rounded-xl bg-[#141416] min-h-[160px]">
                    <img src={img.image_url} alt={img.caption || `${studio.name} portfolio ${i + 1}`} loading="lazy" className="w-full object-cover hover-zoom-img" />
                  </button>
                </Reveal>
              ))}
            </Masonry>
          </div>
        </section>
      )}

      {/* Packages */}
      {studio.packages?.length > 0 && (
        <section data-testid={PHOTOGRAPHER_PROFILE.packagesSection} className="border-t border-[hsl(var(--lxra-border))]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
            <Reveal><SectionHeader kicker="Packages" title="WAYS TO WORK TOGETHER." /></Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {studio.packages.map((p, i) => {
                const highlight = p.id === highlightId;
                return (
                  <Reveal key={p.id} delay={i * 0.05}>
                    <div data-testid={PHOTOGRAPHER_PROFILE.packageCard(p.id)} className={`h-full flex flex-col rounded-2xl border bg-[#0F0F10] p-6 ${highlight ? 'studio-accent-border-strong studio-highlight-pulse' : 'border-[hsl(var(--lxra-border))]'}`}>
                      {highlight && <p className="text-[10px] uppercase tracking-[0.18em] studio-accent-text mb-2">Most requested</p>}
                      <h3 className="font-display text-2xl tracking-[0.06em] text-[hsl(var(--lxra-fg))]">{p.name}</h3>
                      {p.duration_label && <p className="mt-1 text-xs text-[hsl(var(--lxra-muted))]">{p.duration_label}</p>}
                      <p className="mt-4 text-3xl text-[hsl(var(--lxra-fg))] lxra-mono">{p.price_cents ? money(p.price_cents) : 'Custom'}</p>
                      {p.deposit_cents > 0 && <p className="text-[11px] text-[hsl(var(--lxra-muted))]">{money(p.deposit_cents)} deposit to reserve</p>}
                      {p.description && <p className="mt-3 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">{p.description}</p>}
                      {p.includes?.length > 0 && (
                        <ul className="mt-4 space-y-2">
                          {p.includes.map((inc) => <li key={inc} className="flex items-start gap-2 text-sm text-[hsl(var(--lxra-fg))]"><Check className="h-4 w-4 studio-accent-text flex-shrink-0 mt-0.5" />{inc}</li>)}
                        </ul>
                      )}
                      <Link to={`${bookHref}&package=${p.id}`} data-testid={PHOTOGRAPHER_PROFILE.packageBook(p.id)} className={`mt-auto pt-6 inline-flex items-center justify-center h-11 rounded-xl text-xs uppercase tracking-[0.1em] font-semibold ${highlight ? 'studio-solid-btn' : 'studio-outline-btn'}`}>
                        Request this package
                      </Link>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Public galleries */}
      {studio.galleries?.length > 0 && (
        <section data-testid={PHOTOGRAPHER_PROFILE.galleriesSection} className="border-t border-[hsl(var(--lxra-border))]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
            <Reveal><SectionHeader kicker="Galleries" title="RECENT COLLECTIONS." /></Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {studio.galleries.map((g, i) => (
                <Reveal key={g.slug} delay={i * 0.05}>
                  <Link to={`/galleries/${g.slug}`} data-testid={PHOTOGRAPHER_PROFILE.galleryCard(g.slug)} className="group block overflow-hidden rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] hover:border-[hsl(var(--lxra-gold))]/40 transition-colors">
                    <div className="aspect-[4/3] overflow-hidden"><img src={g.cover_image_url} alt={g.name} loading="lazy" className="h-full w-full object-cover hover-zoom-img" /></div>
                    <div className="p-5 flex items-center justify-between">
                      <div><h3 className="font-display text-xl tracking-[0.06em] text-[hsl(var(--lxra-fg))]">{g.name}</h3><p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">{g.image_count} photo{g.image_count === 1 ? '' : 's'}</p></div>
                      <ArrowRight className="h-4 w-4 text-[hsl(var(--lxra-muted))] group-hover:text-[hsl(var(--lxra-gold))] transition-colors" />
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reviews */}
      {reviews?.count > 0 && (
        <section data-testid={PHOTOGRAPHER_PROFILE.reviewsSection} className="border-t border-[hsl(var(--lxra-border))]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
            <Reveal>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <SectionHeader kicker="Reviews" title="WHAT CLIENTS SAY." />
                <span className="flex items-center gap-2 text-sm text-[hsl(var(--lxra-fg))] -mt-8">
                  <StarRating value={reviews.average} size="h-5 w-5" />
                  <span className="lxra-mono text-base">{reviews.average}</span>
                  <span className="text-[hsl(var(--lxra-muted))]">({reviews.count} review{reviews.count === 1 ? '' : 's'})</span>
                </span>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.reviews.map((r, i) => (
                <Reveal key={r.id} delay={i * 0.04}>
                  <div data-testid={PHOTOGRAPHER_PROFILE.reviewCard(r.id)} className="h-full rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-6">
                    <StarRating value={r.rating} size="h-4 w-4" />
                    {r.comment && <p className="mt-3 text-sm text-[hsl(var(--lxra-fg))] leading-relaxed">{r.comment}</p>}
                    <p className="mt-4 text-xs text-[hsl(var(--lxra-muted))]">{r.client_name || 'A client'} · {new Date(r.created_at).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="border-t studio-accent-border">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
          <Reveal>
            <p className="text-xs tracking-[0.2em] uppercase studio-accent-text">Ready when you are</p>
            <h2 className="font-display text-3xl sm:text-4xl mt-2 tracking-[0.06em] text-[hsl(var(--lxra-fg))]">BOOK {studio.name.toUpperCase()}.</h2>
            <p className="mt-3 max-w-md text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">Send a date request — it goes straight to {studio.name}, who confirms availability with you directly.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to={bookHref} className="studio-solid-btn inline-flex items-center h-12 px-7 rounded-xl text-xs uppercase tracking-[0.1em] font-semibold whitespace-nowrap">Request to book <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Reveal>
        </div>
      </section>

      <Lightbox images={portfolioImages} index={lightbox} onClose={() => setLightbox(null)} onNavigate={setLightbox} />
    </main>
  );
}
